import type {
  AgentNotificationKey,
  AgentNotificationPayload,
  AgentRequestKey,
  AgentRequestPayload,
  AgentResponsePayload,
  AutocompleteRequest,
  AutocompleteResponse,
  ChatRequestKey,
  ChatRequestPayload,
  ChatResponsePayload,
  ErrorPayload,
  RaclerMessage,
  ServerRequestKey,
  ServerRequestPayload,
  ServerResponsePayload,
} from "@racler/protocol";
import { MAX_BUFFERED_AMOUNT_THRESHOLD } from "@racler/shared";

export class RelayError extends Error {
  readonly code: ErrorPayload["code"];
  constructor(code: ErrorPayload["code"], message: string) {
    super(message);
    this.code = code;
  }
}

export type ConnectionState = "connecting" | "open" | "closed";
type Listener = (msg: RaclerMessage) => void;

interface PendingRequest {
  resolve: (payload: unknown) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

function split(key: string): { channel: RaclerMessage["channel"]; type: string } {
  const i = key.indexOf("/");
  return { channel: key.slice(0, i) as RaclerMessage["channel"], type: key.slice(i + 1) };
}

/**
 * The single multiplexed WebSocket of this tab (spec §5). Reconnects automatically; on reconnect,
 * `onReconnect` listeners re-subscribe machines and re-attach terminals.
 */
export class RelayClient {
  private ws: WebSocket | null = null;
  private seq = 0;
  private readonly pending = new Map<string, PendingRequest>();
  private readonly listeners = new Set<Listener>();
  private readonly stateListeners = new Set<(s: ConnectionState) => void>();
  private readonly reconnectListeners = new Set<() => void>();
  private state: ConnectionState = "closed";
  private retry = 0;
  private stopped = false;
  private hasConnectedBefore = false;
  private readonly url: string;

  constructor(url: string = RelayClient.defaultUrl()) {
    this.url = url;
  }

  static defaultUrl(): string {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${location.host}/ws/browser`;
  }

  connect(): void {
    this.stopped = false;
    this.setState("connecting");
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      this.setState("open");
      if (this.hasConnectedBefore) for (const l of this.reconnectListeners) l();
      this.hasConnectedBefore = true;
    };
    ws.onmessage = (ev: MessageEvent<string>) => this.onMessage(ev.data);
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.setState("closed");
      for (const [id, p] of this.pending) {
        clearTimeout(p.timer);
        p.reject(new RelayError("MACHINE_OFFLINE", "connection lost"));
        this.pending.delete(id);
      }
      // 4001 = logged out, 1008/4003 = rejected: do not hammer the server.
      if (this.stopped || ev.code === 4001) return;
      const delay = Math.min(15_000, 500 * 2 ** this.retry++) * (0.5 + Math.random() / 2);
      setTimeout(() => this.connect(), delay);
    };
  }

  close(): void {
    this.stopped = true;
    this.ws?.close(1000);
  }

  get connectionState(): ConnectionState {
    return this.state;
  }

  onState(fn: (s: ConnectionState) => void): () => void {
    this.stateListeners.add(fn);
    return () => this.stateListeners.delete(fn);
  }

  onReconnect(fn: () => void): () => void {
    this.reconnectListeners.add(fn);
    return () => this.reconnectListeners.delete(fn);
  }

  /** Listen for events (terminal output, presence, ...). */
  on(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Request answered by the server itself. */
  server<K extends ServerRequestKey>(
    key: K,
    payload: ServerRequestPayload<K>,
  ): Promise<ServerResponsePayload<K>> {
    return this.send(key, payload, undefined) as Promise<ServerResponsePayload<K>>;
  }

  /** Request executed by an agent (the server checks ownership of `machineId`). */
  machine<K extends AgentRequestKey>(
    machineId: string,
    key: K,
    payload: AgentRequestPayload<K>,
  ): Promise<AgentResponsePayload<K>> {
    return this.send(key, payload, machineId) as Promise<AgentResponsePayload<K>>;
  }

  /** Chat request answered by the server (sessions, messages, send, cancel); ownership is checked server-side. */
  chat<K extends ChatRequestKey>(
    machineId: string,
    key: K,
    payload: ChatRequestPayload<K>,
  ): Promise<ChatResponsePayload<K>> {
    return this.send(key, payload, machineId) as Promise<ChatResponsePayload<K>>;
  }

  /** Ephemeral AI autocomplete request with abort signal support. */
  autocomplete(
    machineId: string,
    payload: AutocompleteRequest,
    signal?: AbortSignal,
  ): Promise<AutocompleteResponse> {
    return this.send("agent/ai.autocomplete", payload, machineId, 15_000, signal) as Promise<AutocompleteResponse>;
  }

  cancelAutocomplete(machineId: string, requestId: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.send("agent/ai.autocomplete.cancel", { requestId }, machineId, 5000).catch(() => undefined);
  }

  notify<K extends AgentNotificationKey>(
    machineId: string,
    key: K,
    payload: AgentNotificationPayload<K>,
  ): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const { channel, type } = split(key);
    this.ws.send(JSON.stringify({ id: `n${++this.seq}`, channel, type, payload, machineId }));
  }

  get bufferedAmount(): number {
    return this.ws?.bufferedAmount ?? 0;
  }

  sendBinary(data: ArrayBufferView | ArrayBuffer): void {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      throw new RelayError("MACHINE_OFFLINE", "not connected to the server");
    }
    this.ws.send(data);
  }

  async waitForDrain(threshold: number = MAX_BUFFERED_AMOUNT_THRESHOLD): Promise<void> {
    while (this.ws && this.ws.readyState === WebSocket.OPEN && this.ws.bufferedAmount > threshold) {
      await new Promise((resolve) => setTimeout(resolve, 20));
    }
  }

  private send(
    key: string,
    payload: unknown,
    machineId: string | undefined,
    timeoutMs = 65_000,
    signal?: AbortSignal,
  ): Promise<unknown> {
    const ws = this.ws;
    if (ws?.readyState !== WebSocket.OPEN) {
      return Promise.reject(new RelayError("MACHINE_OFFLINE", "not connected to the server"));
    }
    if (signal?.aborted) {
      return Promise.reject(new DOMException("The request was aborted", "AbortError"));
    }
    const { channel, type } = split(key);
    const id = `r${++this.seq}`;
    return new Promise((resolve, reject) => {
      const onAbort = () => {
        clearTimeout(timer);
        this.pending.delete(id);
        reject(new DOMException("The request was aborted", "AbortError"));
      };
      if (signal) {
        signal.addEventListener("abort", onAbort, { once: true });
      }
      const timer = setTimeout(() => {
        if (signal) signal.removeEventListener("abort", onAbort);
        this.pending.delete(id);
        reject(new RelayError("TIMEOUT", "request timed out"));
      }, timeoutMs);
      this.pending.set(id, {
        resolve: (val) => {
          if (signal) signal.removeEventListener("abort", onAbort);
          resolve(val);
        },
        reject: (err) => {
          if (signal) signal.removeEventListener("abort", onAbort);
          reject(err);
        },
        timer,
      });
      const msg: RaclerMessage = { id, channel, type, payload };
      if (machineId) msg.machineId = machineId;
      ws.send(JSON.stringify(msg));
    });
  }

  private onMessage(raw: string): void {
    let msg: RaclerMessage;
    try {
      msg = JSON.parse(raw) as RaclerMessage;
    } catch {
      return;
    }
    const p = this.pending.get(msg.id);
    if (p && (msg.type === "error" || msg.type.endsWith(".result"))) {
      this.pending.delete(msg.id);
      clearTimeout(p.timer);
      if (msg.type === "error") {
        const err = msg.payload as ErrorPayload;
        p.reject(new RelayError(err.code, err.message));
      } else {
        p.resolve(msg.payload);
      }
      return;
    }
    for (const l of this.listeners) l(msg);
  }

  private setState(s: ConnectionState): void {
    this.state = s;
    for (const l of this.stateListeners) l(s);
  }
}
